import { create } from 'zustand';
import { providerService } from '../services/providers';
import type { ApiError, ProviderContext } from '../types';

const ACTIVE_PROVIDER_STORAGE_KEY = 'marketplace_active_transport_provider_id';

interface ProviderContextState {
    contexts: ProviderContext[];
    activeContext: ProviderContext | null;
    contextsLoaded: boolean;
    isLoading: boolean;
    error: ApiError | null;
    requiresSelection: boolean;
    loadContexts: () => Promise<void>;
    selectContext: (providerId: number) => Promise<boolean>;
    clearContext: () => void;
}

function readStoredProviderId(): number | null {
    const rawValue = localStorage.getItem(ACTIVE_PROVIDER_STORAGE_KEY);
    const providerId = rawValue ? Number(rawValue) : 0;

    return Number.isInteger(providerId) && providerId > 0 ? providerId : null;
}

function findOperableContext(contexts: ProviderContext[], providerId: number | null): ProviderContext | null {
    if (providerId === null) {
        return null;
    }

    return contexts.find(context => context.transport_provider_id === providerId && context.can_operate) ?? null;
}

function resolveInitialActiveContext(contexts: ProviderContext[], defaultProviderId: number | null): ProviderContext | null {
    const storedContext = findOperableContext(contexts, readStoredProviderId());

    if (storedContext) {
        return storedContext;
    }

    const defaultContext = findOperableContext(contexts, defaultProviderId);

    if (defaultContext) {
        localStorage.setItem(ACTIVE_PROVIDER_STORAGE_KEY, String(defaultContext.transport_provider_id));
        return defaultContext;
    }

    localStorage.removeItem(ACTIVE_PROVIDER_STORAGE_KEY);
    return null;
}

export const useProviderContextStore = create<ProviderContextState>((set, get) => ({
    contexts: [],
    activeContext: null,
    contextsLoaded: false,
    isLoading: false,
    error: null,
    requiresSelection: false,

    loadContexts: async (): Promise<void> => {
        set({ isLoading: true, error: null });

        try {
            const response = await providerService.listProviderContexts();
            const activeContext = resolveInitialActiveContext(
                response.data,
                response.meta.default_transport_provider_id,
            );

            set({
                contexts: response.data,
                activeContext,
                requiresSelection: response.meta.requires_selection && activeContext === null,
                contextsLoaded: true,
                isLoading: false,
                error: null,
            });
        } catch (err) {
            const error = err as ApiError;
            set({
                isLoading: false,
                contextsLoaded: true,
                error: error.message ? error : { message: 'Error al cargar contextos de proveedor' },
            });
        }
    },

    selectContext: async (providerId: number): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
            const response = await providerService.selectProviderContext(providerId);
            const existingContext = get().contexts.find(
                context => context.transport_provider_id === response.data.transport_provider_id,
            );
            const activeContext = existingContext ?? response.data;

            localStorage.setItem(ACTIVE_PROVIDER_STORAGE_KEY, String(activeContext.transport_provider_id));

            set({
                activeContext,
                contextsLoaded: true,
                isLoading: false,
                error: null,
                requiresSelection: false,
            });

            return true;
        } catch (err) {
            const error = err as ApiError;
            localStorage.removeItem(ACTIVE_PROVIDER_STORAGE_KEY);
            set({
                activeContext: null,
                contextsLoaded: true,
                isLoading: false,
                error: error.message ? error : { message: 'Error al seleccionar contexto de proveedor' },
            });

            return false;
        }
    },

    clearContext: (): void => {
        localStorage.removeItem(ACTIVE_PROVIDER_STORAGE_KEY);
        set({
            contexts: [],
            activeContext: null,
            contextsLoaded: false,
            isLoading: false,
            error: null,
            requiresSelection: false,
        });
    },
}));

export { ACTIVE_PROVIDER_STORAGE_KEY };
